import { addErrorToReport, updateLocale } from '@americanexpress/one-app-ducks';
import setAXPLocaleCookie from './setAXPLocaleCookie';

/**
 * Splits a locale string into its language and country parts.
 * @param {string} locale - The locale string in the format 'lang-country'.
 * @returns {Object} An object with the language and country properties.
 * @example
 * splitLocale('en-US') // { lang: 'en', country: 'US' }
 * splitLocale('en') // { lang: null, country: null } // invalid locale
 * splitLocale('en-us') // { lang: 'en', country: 'US' }
 * // etc.
 */
export const splitLocale = (locale) => {
  const isValidLocale = /^[A-Za-z]{2}-[A-Za-z]{2}$/.test(locale);

  if (!isValidLocale) {
    // This allows check for invalid locale strings
    return {
      lang: null,
      country: null,
    };
  }

  const [lang, country] = String(locale).split('-');
  return {
    lang,
    country: country?.toUpperCase(),
  };
};

/**
 * country code to locale mapper
 */
const localeMapper = {
  en: 'en-US',
  us: 'en-US',
  jp: 'ja-JP',
  ja: 'ja-JP',
  au: 'en-AU',
  uk: 'en-GB',
};
/**
 * extract the locale from current url
 * @param {Object} location
 * @param {String} fallbackLocale
 * @returns
 */
export const getLocaleFromLocation = () => {
  if (typeof window === 'undefined') return null;
  // this is very flawed. it assumes the first part of the path is the locale
  return window.location.pathname.split('/').filter(Boolean)[0] || null;
};

/**
 * Sets the active locale for the application.
 * intended use is in a route onEnter hook.
 * This helps the application set the redux state for active locale.
 * This also calls the setAXPLocaleCookie function to set the locale in the cookie.
 * this cookie is used by dtm and other services to determine the locale of the user.
 * @param {Object} store - The Redux store.
 * @param {string} locale - The locale string in the format 'lang-country'.
 * @example
 * <ModuleRoute onEnter={setActiveLocale(store, 'en-us')}></ModuleRoute>
 */
const setActiveLocale = (store, targetLocale) => {
  if (!store) return () => console.error('setActiveLocale error: store is required');

  const { dispatch } = store;
  const defaultLocale = 'en-US';

  return async () => {
    const localeFromLocationURL = getLocaleFromLocation();

    // locale mapper allow for partial matches such as 'en' to 'en-US', and 'uk' to 'en-GB'
    const locale = localeMapper[targetLocale]
      || localeMapper[localeFromLocationURL]
      || targetLocale
      || localeFromLocationURL;

    const { lang, country } = splitLocale(locale);

    if (!lang || !country) {
      // - this will happend when the locale is not in the format 'lang-country'
      // - this will also happen if the language is not supported by the localeMapper
      dispatch(updateLocale(defaultLocale));
      return;
    }

    try {
      const newLocale = `${lang}-${country}`;
      await dispatch(updateLocale(newLocale));
      setAXPLocaleCookie(newLocale);
    } catch (error) {
      dispatch(addErrorToReport(error));
      dispatch(updateLocale(defaultLocale));
      setAXPLocaleCookie(defaultLocale);
    }
  };
};

export default setActiveLocale;
